import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { objectivesAPI, financeAPI, bankDataAPI, quipuAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Target,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  AlertTriangle,
  Edit,
  Save,
  BarChart3,
  PieChart,
  Calendar,
  Loader2,
  Eye,
  Wallet,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  Trash2,
  RefreshCw,
  X,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export function ObjectivesPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Objetivos desde la base de datos
  const [objectives, setObjectives] = useState(null);
  
  // KPIs automáticos
  const [autoKPIs, setAutoKPIs] = useState({
    activeClients: 0,
    totalLeads: 0,
    convertedLeads: 0,
    closeRate: 0,
    netTreasury: 0,
    clientsList: []
  });
  
  // Datos de Quipu y Banco
  const [quipuData, setQuipuData] = useState({ monthlyRevenue: 0, monthlyExpenses: 0 });
  const [bankData, setBankData] = useState({ balance: 0 });
  
  // Estados de edición
  const [editingVision, setEditingVision] = useState(false);
  const [editingOKRs, setEditingOKRs] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [editingProfitability, setEditingProfitability] = useState(false);
  const [editingForecast, setEditingForecast] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [objData, kpisData] = await Promise.all([
        objectivesAPI.get(),
        objectivesAPI.getKPIs()
      ]);
      setObjectives(objData);
      setAutoKPIs(kpisData);
      
      // Cargar datos de finanzas
      try {
        const [quipu, bank] = await Promise.all([
          quipuAPI.getFinanceSummary(),
          bankDataAPI.getSummary()
        ]);
        if (quipu) {
          setQuipuData({
            monthlyRevenue: quipu.this_month?.income || 0,
            monthlyExpenses: quipu.this_month?.expenses || 0
          });
        }
        if (bank) {
          setBankData({ balance: bank.balance || 0 });
        }
      } catch (e) {
        console.error('Error loading finance data:', e);
      }
    } catch (error) {
      console.error('Error loading objectives:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    await loadData();
    setSyncing(false);
  };

  const saveObjectives = async (updates) => {
    setSaving(true);
    try {
      const res = await objectivesAPI.update(updates);
      // Sync from the server's authoritative response to avoid stale flicker
      const persisted = res?.data || updates;
      setObjectives(prev => ({ ...prev, ...persisted }));
      return true;
    } catch (error) {
      console.error('Error saving objectives:', error);
      toast.error('Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Vision handlers
  const handleSaveVision = async () => {
    const ok = await saveObjectives({ vision: objectives.vision });
    if (ok) setEditingVision(false);
  };

  // OKR handlers
  const addOKR = () => {
    const newOKR = {
      id: Date.now().toString(),
      objective: 'Nuevo Objetivo',
      keyResults: [{ title: 'Nuevo Key Result', current: 0, target: 1 }]
    };
    setObjectives(prev => ({
      ...prev,
      okrs: [...(prev.okrs || []), newOKR]
    }));
  };

  const removeOKR = (okrId) => {
    setObjectives(prev => ({
      ...prev,
      okrs: prev.okrs.filter(o => o.id !== okrId)
    }));
  };

  const updateOKR = (okrId, field, value) => {
    setObjectives(prev => ({
      ...prev,
      okrs: prev.okrs.map(o => o.id === okrId ? { ...o, [field]: value } : o)
    }));
  };

  const addKeyResult = (okrId) => {
    setObjectives(prev => ({
      ...prev,
      okrs: prev.okrs.map(o => o.id === okrId ? {
        ...o,
        keyResults: [...o.keyResults, { title: 'Nuevo KR', current: 0, target: 1 }]
      } : o)
    }));
  };

  const removeKeyResult = (okrId, krIndex) => {
    setObjectives(prev => ({
      ...prev,
      okrs: prev.okrs.map(o => o.id === okrId ? {
        ...o,
        keyResults: o.keyResults.filter((_, i) => i !== krIndex)
      } : o)
    }));
  };

  const updateKeyResult = (okrId, krIndex, field, value) => {
    setObjectives(prev => ({
      ...prev,
      okrs: prev.okrs.map(o => o.id === okrId ? {
        ...o,
        keyResults: o.keyResults.map((kr, i) => i === krIndex ? { ...kr, [field]: value } : kr)
      } : o)
    }));
  };

  const handleSaveOKRs = async () => {
    const ok = await saveObjectives({ okrs: objectives.okrs });
    if (ok) setEditingOKRs(false);
  };

  // Team Capacity handlers
  const addDepartment = () => {
    const newDept = { name: 'Nuevo Departamento', available: 160, committed: 0 };
    setObjectives(prev => ({
      ...prev,
      teamCapacity: {
        ...prev.teamCapacity,
        departments: [...(prev.teamCapacity?.departments || []), newDept]
      }
    }));
  };

  const removeDepartment = (index) => {
    setObjectives(prev => ({
      ...prev,
      teamCapacity: {
        ...prev.teamCapacity,
        departments: prev.teamCapacity.departments.filter((_, i) => i !== index)
      }
    }));
  };

  const updateDepartment = (index, field, value) => {
    setObjectives(prev => ({
      ...prev,
      teamCapacity: {
        ...prev.teamCapacity,
        departments: prev.teamCapacity.departments.map((d, i) => 
          i === index ? { ...d, [field]: field === 'name' ? value : parseFloat(value) || 0 } : d
        )
      }
    }));
  };

  const handleSaveCapacity = async () => {
    // Recalculate total hours
    const totalHours = objectives.teamCapacity.departments.reduce((sum, d) => sum + (d.available || 0), 0);
    const ok = await saveObjectives({ 
      teamCapacity: { 
        ...objectives.teamCapacity, 
        totalHours 
      } 
    });
    if (ok) setEditingCapacity(false);
  };

  // Client profitability handlers
  const updateClientProfitability = (clientId, field, value) => {
    setObjectives(prev => ({
      ...prev,
      clientProfitability: (prev.clientProfitability || []).map(cp => 
        cp.clientId === clientId ? { ...cp, [field]: parseFloat(value) || 0 } : cp
      )
    }));
  };

  const initClientProfitability = () => {
    // Initialize from clients list
    const newProfitability = autoKPIs.clientsList.map(c => ({
      clientId: c.id,
      clientName: c.name,
      cost: 0,
      hours: 0
    }));
    setObjectives(prev => ({ ...prev, clientProfitability: newProfitability }));
  };

  const handleSaveProfitability = async () => {
    const ok = await saveObjectives({ clientProfitability: objectives.clientProfitability });
    if (ok) setEditingProfitability(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Calculated values
  const vision = objectives?.vision || {};
  const okrs = objectives?.okrs || [];
  const teamCapacity = objectives?.teamCapacity || { totalHours: 0, departments: [] };
  const clientProfitability = objectives?.clientProfitability || [];
  const forecast3Months = objectives?.forecast3Months || 0;
  
  const monthlyRevenue = quipuData.monthlyRevenue;
  const monthlyExpenses = quipuData.monthlyExpenses;
  const monthlyProfit = monthlyRevenue - monthlyExpenses;
  const marginPercent = monthlyRevenue > 0 ? ((monthlyProfit / monthlyRevenue) * 100).toFixed(1) : 0;
  const netTreasury = bankData.balance;
  const avgTicket = autoKPIs.activeClients > 0 ? (monthlyRevenue / autoKPIs.activeClients).toFixed(0) : 0;
  
  const totalCommittedHours = teamCapacity.departments.reduce((sum, d) => sum + (d.committed || 0), 0);
  const totalAvailableHours = teamCapacity.departments.reduce((sum, d) => sum + (d.available || 0), 0);
  const occupationRate = totalAvailableHours > 0 ? ((totalCommittedHours / totalAvailableHours) * 100).toFixed(1) : 0;
  const bottleneckDept = teamCapacity.departments.reduce((max, d) => 
    (d.available > 0 && (d.committed / d.available) > (max?.committed / max?.available || 0)) ? d : max
  , teamCapacity.departments[0]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="objectives-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Objetivos</h1>
          <p className="text-muted-foreground mt-1">Visión, OKRs y métricas clave de la empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>
          <Badge variant="outline" className="text-primary border-primary">
            <Calendar className="w-3 h-3 mr-1" />
            Q1 2026
          </Badge>
        </div>
      </motion.div>

      {/* Visión y Objetivos Anuales */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary" />
                  Visión y Objetivos Anuales
                </CardTitle>
                <CardDescription>Metas estratégicas para 2026</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => editingVision ? handleSaveVision() : setEditingVision(true)} disabled={saving}>
                {editingVision ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                {editingVision ? 'Guardar' : 'Editar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Facturación Anual */}
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Facturación Anual</span>
                </div>
                {editingVision ? (
                  <Input
                    type="number"
                    value={vision.annualRevenue || 0}
                    onChange={(e) => setObjectives(prev => ({
                      ...prev,
                      vision: { ...prev.vision, annualRevenue: parseInt(e.target.value) || 0 }
                    }))}
                    className="mt-2"
                  />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-emerald-700">€{(vision.annualRevenue || 0).toLocaleString()}</p>
                    <Progress value={vision.annualRevenue > 0 ? (monthlyRevenue * 12 / vision.annualRevenue) * 100 : 0} className="h-2 mt-2" />
                    <p className="text-xs text-emerald-600 mt-1">
                      {vision.annualRevenue > 0 ? ((monthlyRevenue * 12 / vision.annualRevenue) * 100).toFixed(0) : 0}% proyectado
                    </p>
                  </>
                )}
              </div>

              {/* Beneficio Objetivo */}
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Beneficio Objetivo</span>
                </div>
                {editingVision ? (
                  <Input
                    type="number"
                    value={vision.annualProfit || 0}
                    onChange={(e) => setObjectives(prev => ({
                      ...prev,
                      vision: { ...prev.vision, annualProfit: parseInt(e.target.value) || 0 }
                    }))}
                    className="mt-2"
                  />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-blue-700">€{(vision.annualProfit || 0).toLocaleString()}</p>
                    <Progress value={vision.annualProfit > 0 ? (monthlyProfit * 12 / vision.annualProfit) * 100 : 0} className="h-2 mt-2" />
                    <p className="text-xs text-blue-600 mt-1">
                      {vision.annualProfit > 0 ? ((monthlyProfit * 12 / vision.annualProfit) * 100).toFixed(0) : 0}% proyectado
                    </p>
                  </>
                )}
              </div>

              {/* Clientes Ideales */}
              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Clientes Ideales</span>
                </div>
                {editingVision ? (
                  <Input
                    type="number"
                    value={vision.idealClients || 0}
                    onChange={(e) => setObjectives(prev => ({
                      ...prev,
                      vision: { ...prev.vision, idealClients: parseInt(e.target.value) || 0 }
                    }))}
                    className="mt-2"
                  />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-purple-700">{vision.idealClients || 0}</p>
                    <Progress value={vision.idealClients > 0 ? (autoKPIs.activeClients / vision.idealClients) * 100 : 0} className="h-2 mt-2" />
                    <p className="text-xs text-purple-600 mt-1">
                      {autoKPIs.activeClients} actuales ({vision.idealClients > 0 ? ((autoKPIs.activeClients / vision.idealClients) * 100).toFixed(0) : 0}%)
                    </p>
                  </>
                )}
              </div>

              {/* Ticket Medio */}
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">Ticket Medio Objetivo</span>
                </div>
                {editingVision ? (
                  <Input
                    type="number"
                    value={vision.averageTicket || 0}
                    onChange={(e) => setObjectives(prev => ({
                      ...prev,
                      vision: { ...prev.vision, averageTicket: parseInt(e.target.value) || 0 }
                    }))}
                    className="mt-2"
                  />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-amber-700">€{(vision.averageTicket || 0).toLocaleString()}</p>
                    <Progress value={vision.averageTicket > 0 ? (avgTicket / vision.averageTicket) * 100 : 0} className="h-2 mt-2" />
                    <p className="text-xs text-amber-600 mt-1">
                      €{avgTicket} actual ({vision.averageTicket > 0 ? ((avgTicket / vision.averageTicket) * 100).toFixed(0) : 0}%)
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* OKRs Trimestrales */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  OKRs Trimestrales
                </CardTitle>
                <CardDescription>Objetivos y Resultados Clave Q1 2026</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {editingOKRs && (
                  <Button variant="outline" size="sm" onClick={addOKR}>
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir OKR
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => editingOKRs ? handleSaveOKRs() : setEditingOKRs(true)} disabled={saving}>
                  {editingOKRs ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                  {editingOKRs ? 'Guardar' : 'Editar'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {okrs.map((okr, index) => {
                const avgProgress = okr.keyResults.length > 0 
                  ? okr.keyResults.reduce((sum, kr) => sum + (kr.target > 0 ? (kr.current / kr.target) * 100 : 0), 0) / okr.keyResults.length
                  : 0;
                
                return (
                  <div key={okr.id || index} className="p-4 rounded-xl bg-muted/30 border relative">
                    {editingOKRs && (
                      <button
                        onClick={() => removeOKR(okr.id)}
                        className="absolute top-2 right-2 p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    
                    {editingOKRs ? (
                      <Input
                        value={okr.objective}
                        onChange={(e) => updateOKR(okr.id, 'objective', e.target.value)}
                        className="font-semibold text-lg mb-4"
                        placeholder="Nombre del objetivo"
                      />
                    ) : (
                      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${index % 2 === 0 ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                        {okr.objective}
                        <Badge variant="outline" className="ml-auto">{avgProgress.toFixed(0)}%</Badge>
                      </h3>
                    )}
                    
                    <div className="space-y-4">
                      {okr.keyResults.map((kr, krIndex) => {
                        const progress = kr.target > 0 ? (kr.current / kr.target) * 100 : 0;
                        
                        return (
                          <div key={krIndex} className="relative">
                            {editingOKRs ? (
                              <div className="space-y-2 p-3 bg-background rounded-lg border">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={kr.title}
                                    onChange={(e) => updateKeyResult(okr.id, krIndex, 'title', e.target.value)}
                                    placeholder="Key Result"
                                    className="flex-1"
                                  />
                                  <button
                                    onClick={() => removeKeyResult(okr.id, krIndex)}
                                    className="p-1 hover:bg-red-100 rounded text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">Actual:</span>
                                  <Input
                                    type="number"
                                    value={kr.current}
                                    onChange={(e) => updateKeyResult(okr.id, krIndex, 'current', parseFloat(e.target.value) || 0)}
                                    className="w-20"
                                  />
                                  <span className="text-sm text-muted-foreground">/ Objetivo:</span>
                                  <Input
                                    type="number"
                                    value={kr.target}
                                    onChange={(e) => updateKeyResult(okr.id, krIndex, 'target', parseFloat(e.target.value) || 1)}
                                    className="w-20"
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm">{kr.title}</span>
                                  <span className="text-sm font-medium">{kr.current}/{kr.target}</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                                <p className="text-xs text-muted-foreground mt-1">{progress.toFixed(0)}% completado</p>
                              </>
                            )}
                          </div>
                        );
                      })}
                      
                      {editingOKRs && (
                        <Button variant="ghost" size="sm" onClick={() => addKeyResult(okr.id)} className="w-full">
                          <Plus className="w-4 h-4 mr-1" />
                          Añadir Key Result
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* KPIs Globales */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  KPIs Globales
                </CardTitle>
                <CardDescription>Métricas automáticas del mes actual</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {editingForecast ? (
                  <>
                    <Input
                      type="number"
                      value={forecast3Months}
                      onChange={(e) => setObjectives(prev => ({ ...prev, forecast3Months: parseFloat(e.target.value) || 0 }))}
                      className="w-32"
                      placeholder="Previsión"
                    />
                    <Button size="sm" onClick={async () => { const ok = await saveObjectives({ forecast3Months: objectives.forecast3Months }); if (ok) setEditingForecast(false); }}>
                      <Save className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    Datos sincronizados automáticamente
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="p-4 rounded-xl bg-emerald-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Facturación Mes</p>
                <p className="text-xl font-bold text-emerald-700">€{monthlyRevenue.toLocaleString()}</p>
                <p className="text-xs text-emerald-600">Quipu</p>
              </div>
              <div className="p-4 rounded-xl bg-blue-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Beneficio Mes</p>
                <p className="text-xl font-bold text-blue-700">€{monthlyProfit.toLocaleString()}</p>
                <p className="text-xs text-blue-600">Quipu</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Margen %</p>
                <p className="text-xl font-bold text-purple-700">{marginPercent}%</p>
                <p className="text-xs text-purple-600">Calculado</p>
              </div>
              <div className="p-4 rounded-xl bg-amber-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Leads</p>
                <p className="text-xl font-bold text-amber-700">{autoKPIs.totalLeads}</p>
                <p className="text-xs text-amber-600">CRM</p>
              </div>
              <div className="p-4 rounded-xl bg-red-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">% Cierre</p>
                <p className="text-xl font-bold text-red-600">{autoKPIs.closeRate.toFixed(1)}%</p>
                <p className="text-xs text-red-600">Calculado</p>
              </div>
              <div className="p-4 rounded-xl bg-teal-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Clientes Activos</p>
                <p className="text-xl font-bold text-teal-700">{autoKPIs.activeClients}</p>
                <p className="text-xs text-teal-600">CRM</p>
              </div>
              <div className="p-4 rounded-xl bg-indigo-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Ticket Medio</p>
                <p className="text-xl font-bold text-indigo-700">€{avgTicket}</p>
                <p className="text-xs text-indigo-600">Calculado</p>
              </div>
              <div className="p-4 rounded-xl bg-green-50 text-center">
                <p className="text-xs text-muted-foreground mb-1">Tesorería Neta</p>
                <p className="text-xl font-bold text-green-700">€{netTreasury.toLocaleString()}</p>
                <p className="text-xs text-green-600">Fintable</p>
              </div>
              <div className="p-4 rounded-xl bg-orange-50 text-center col-span-2 md:col-span-1 cursor-pointer hover:bg-orange-100" onClick={() => setEditingForecast(true)}>
                <p className="text-xs text-muted-foreground mb-1">Previsión 3 Meses</p>
                <p className="text-xl font-bold text-orange-700">€{(forecast3Months || monthlyRevenue * 3).toLocaleString()}</p>
                <p className="text-xs text-orange-600">Editable</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Rentabilidad por Cliente */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-primary" />
                  Rentabilidad por Cliente
                </CardTitle>
                <CardDescription>Análisis de margen y horas por cliente (clientes del CRM)</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {clientProfitability.length === 0 && autoKPIs.clientsList.length > 0 && (
                  <Button variant="outline" size="sm" onClick={initClientProfitability}>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Cargar Clientes
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => editingProfitability ? handleSaveProfitability() : setEditingProfitability(true)} disabled={saving}>
                  {editingProfitability ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                  {editingProfitability ? 'Guardar' : 'Editar'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {clientProfitability.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay clientes configurados</p>
                <p className="text-sm">Haz clic en "Cargar Clientes" para importar desde el CRM</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Cliente</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Facturación Mes</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Coste Estimado</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Margen Real</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Horas Invertidas</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">€/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientProfitability.map((client) => {
                      const revenue = (monthlyRevenue / clientProfitability.length); // Placeholder - ideally from Quipu per client
                      const margin = revenue > 0 ? ((revenue - client.cost) / revenue * 100) : 0;
                      const eurPerHour = client.hours > 0 ? ((revenue - client.cost) / client.hours) : 0;
                      
                      return (
                        <tr key={client.clientId} className="border-b hover:bg-muted/30">
                          <td className="py-3 px-4 font-medium">{client.clientName}</td>
                          <td className="py-3 px-4 text-right text-emerald-600 font-medium">€{revenue.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right">
                            {editingProfitability ? (
                              <Input
                                type="number"
                                value={client.cost}
                                onChange={(e) => updateClientProfitability(client.clientId, 'cost', e.target.value)}
                                className="w-24 text-right"
                              />
                            ) : (
                              <span className="text-red-500">€{client.cost.toLocaleString()}</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Badge className={margin >= 40 ? 'bg-emerald-100 text-emerald-700' : margin >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                              {margin.toFixed(1)}%
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {editingProfitability ? (
                              <Input
                                type="number"
                                value={client.hours}
                                onChange={(e) => updateClientProfitability(client.clientId, 'hours', e.target.value)}
                                className="w-20 text-right"
                              />
                            ) : (
                              <span>{client.hours}h</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right font-medium">€{eurPerHour.toFixed(0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Capacidad del Equipo */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Capacidad del Equipo
                </CardTitle>
                <CardDescription>Disponibilidad y ocupación del equipo</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {editingCapacity && (
                  <Button variant="outline" size="sm" onClick={addDepartment}>
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir Dept.
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => editingCapacity ? handleSaveCapacity() : setEditingCapacity(true)} disabled={saving}>
                  {editingCapacity ? <Save className="w-4 h-4 mr-1" /> : <Edit className="w-4 h-4 mr-1" />}
                  {editingCapacity ? 'Guardar' : 'Editar'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Resumen */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-blue-50">
                    <p className="text-xs text-muted-foreground mb-1">Horas Disponibles</p>
                    <p className="text-2xl font-bold text-blue-700">{totalAvailableHours}h</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50">
                    <p className="text-xs text-muted-foreground mb-1">Horas Comprometidas</p>
                    <p className="text-2xl font-bold text-amber-700">{totalCommittedHours}h</p>
                  </div>
                </div>
                
                <div className="p-4 rounded-xl bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">% Ocupación Global</span>
                    <span className="font-bold">{occupationRate}%</span>
                  </div>
                  <Progress value={parseFloat(occupationRate)} className="h-3" />
                </div>

                {parseFloat(occupationRate) > 80 && bottleneckDept && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      <div>
                        <p className="font-medium">Cuello de Botella Detectado</p>
                        <p className="text-sm">{bottleneckDept.name} - {bottleneckDept.available > 0 ? ((bottleneckDept.committed / bottleneckDept.available) * 100).toFixed(0) : 0}% ocupación</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Por departamento */}
              <div className="space-y-3">
                <p className="font-medium text-sm text-muted-foreground">Por Departamento</p>
                {teamCapacity.departments.map((dept, index) => {
                  const deptOccupation = dept.available > 0 ? (dept.committed / dept.available) * 100 : 0;
                  
                  return (
                    <div key={index} className="p-3 rounded-lg bg-muted/30 relative">
                      {editingCapacity && (
                        <button
                          onClick={() => removeDepartment(index)}
                          className="absolute top-2 right-2 p-1 hover:bg-red-100 rounded text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                      
                      {editingCapacity ? (
                        <div className="space-y-2 pr-6">
                          <Input
                            value={dept.name}
                            onChange={(e) => updateDepartment(index, 'name', e.target.value)}
                            placeholder="Nombre departamento"
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-sm">Disponibles:</span>
                            <Input
                              type="number"
                              value={dept.available}
                              onChange={(e) => updateDepartment(index, 'available', e.target.value)}
                              className="w-20"
                            />
                            <span className="text-sm">Comprometidas:</span>
                            <Input
                              type="number"
                              value={dept.committed}
                              onChange={(e) => updateDepartment(index, 'committed', e.target.value)}
                              className="w-20"
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{dept.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{dept.committed}h / {dept.available}h</span>
                              <Badge className={
                                deptOccupation >= 90 ? 'bg-red-100 text-red-700' :
                                deptOccupation >= 75 ? 'bg-amber-100 text-amber-700' :
                                'bg-emerald-100 text-emerald-700'
                              }>
                                {deptOccupation.toFixed(0)}%
                              </Badge>
                            </div>
                          </div>
                          <Progress 
                            value={deptOccupation} 
                            className={`h-2 ${deptOccupation >= 90 ? '[&>div]:bg-red-500' : deptOccupation >= 75 ? '[&>div]:bg-amber-500' : ''}`}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
